import { useEffect, useState } from 'react';

import { Button, Dropdown, FieldLabel, showSnackbar, TextArea, TextInput } from 'fluxo-ui';
import { CheckIcon, EyeIcon, SaveIcon, TimesIcon } from 'fluxo-ui/icons';

import { EXAM_TYPES } from '../../common/constants';
import categoryService from '../../services/category.service';
import { MarkdownEditor } from '../question';

import ExamSettings from './ExamSettings';
import QuestionSelector from './QuestionSelector';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface ExamData {
    id?: string;
    title: string;
    description: string;
    type: string;
    category?: string;
    instructions?: string;
    questions: any[];
    settings: any;
}

interface ExamEditorProps {
    exam?: ExamData;
    onSave?: (exam: ExamData, publish?: boolean) => Promise<void>;
    onCancel?: () => void;
    onPreview?: () => void;
    loading?: boolean;
}

const ExamEditor: React.FC<ExamEditorProps> = ({ exam, onSave, onCancel, onPreview, loading = false }) => {
    const [formData, setFormData] = useState<ExamData>(
        exam || {
            title: '',
            description: '',
            type: EXAM_TYPES.ASSESSMENT,
            questions: [],
            settings: {},
        },
    );
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        if (exam) {
            setFormData(exam);
        }
    }, [exam]);

    const [categories, setCategories] = useState<ListItem[]>([]);

    useEffect(() => {
        categoryService
            .listCategories({ limit: 100 })
            .then((res) => {
                setCategories((res.categories || []).map((c: any) => ({ label: c.name, value: c._id })));
            })
            .catch(() => {});
    }, []);

    const examTypes: ListItem[] = [
        { label: 'Practice', value: EXAM_TYPES.PRACTICE },
        { label: 'Assessment', value: EXAM_TYPES.ASSESSMENT },
        { label: 'Certification', value: EXAM_TYPES.CERTIFICATION },
        { label: 'Survey', value: EXAM_TYPES.SURVEY },
        { label: 'Live Quiz', value: EXAM_TYPES.LIVE_QUIZ },
    ];

    const updateField = <K extends keyof ExamData>(field: K, value: ExamData[K]) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validateBasicInfo = (newErrors: Record<string, string>) => {
        if (!formData.title?.trim()) {
            newErrors.title = 'Exam title is required';
        }
        if (!formData.description?.trim()) {
            newErrors.description = 'Description is required';
        }
        if (!formData.type) {
            newErrors.type = 'Exam type is required';
        }
    };

    const validateQuestions = (newErrors: Record<string, string>) => {
        if (formData.questions.length === 0) {
            newErrors.questions = 'At least one question is required';
        }
    };

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};
        if (step === 0) validateBasicInfo(newErrors);
        if (step === 1) validateQuestions(newErrors);
        setErrors((prev) => ({ ...prev, ...newErrors }));
        return Object.keys(newErrors).length === 0;
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        validateBasicInfo(newErrors);
        validateQuestions(newErrors);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (publish?: boolean) => {
        if (!validate()) {
            showSnackbar('Please fix the errors before saving', 'Validation Error', { type: 'error' });
            return;
        }

        setSaving(true);
        try {
            await onSave?.(formData, publish);
            showSnackbar(publish ? 'Exam published successfully' : 'Exam saved successfully', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to save exam', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const steps = [
        { title: 'Basic Info', description: 'Title, description, and type' },
        { title: 'Questions', description: 'Select and arrange questions' },
        { title: 'Settings', description: 'Configure exam behavior' },
        { title: 'Review', description: 'Review and publish' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                        {exam ? 'Edit Exam' : 'Create New Exam'}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{steps[currentStep].description}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {onCancel && (
                        <Button
                            label="Cancel"
                            leftIcon={TimesIcon}
                            variant="default"
                            layout="outlined"
                            onClick={onCancel}
                            disabled={saving}
                        />
                    )}
                    {onPreview && exam && (
                        <Button
                            label="Preview"
                            leftIcon={EyeIcon}
                            variant="default"
                            layout="outlined"
                            onClick={onPreview}
                            disabled={saving}
                        />
                    )}
                    <Button
                        label="Save Exam"
                        leftIcon={SaveIcon}
                        variant="primary"
                        onClick={() => {
                            handleSave(false);
                        }}
                        isLoading={saving}
                        disabled={saving || loading}
                    />
                </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center gap-2">
                {steps.map((step, index) => {
                    const isActive = currentStep === index;
                    const isDone = index < currentStep;
                    return (
                        <button
                            key={index}
                            onClick={() => {
                                if (index <= currentStep) {
                                    setCurrentStep(index);
                                    return;
                                }
                                for (let step = currentStep; step < index; step++) {
                                    if (!validateStep(step)) {
                                        setCurrentStep(step);
                                        return;
                                    }
                                }
                                setCurrentStep(index);
                            }}
                            className={`flex-1 p-3.5 rounded-xl border transition-colors text-left ${
                                isActive
                                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-sm'
                                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-teal-300 dark:hover:border-slate-600'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                                        isActive
                                            ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white'
                                            : isDone
                                              ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                    }`}
                                >
                                    {isDone ? <CheckIcon className="w-4 h-4" /> : index + 1}
                                </div>
                                <div className="min-w-0">
                                    <div
                                        className={`font-medium text-sm truncate ${
                                            isActive ? 'text-teal-700 dark:text-teal-300' : 'text-slate-900 dark:text-white'
                                        }`}
                                    >
                                        {step.title}
                                    </div>
                                    <div className="hidden lg:block text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {step.description}
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Step Content */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                {currentStep === 0 && (
                    <div className="space-y-4">
                        <FieldLabel label="Exam Title" required error={errors.title}>
                            <TextInput
                                value={formData.title}
                                onChange={(e: ComponentEvent<string>) => updateField('title', e.value)}
                                placeholder="Enter exam title"
                                disabled={loading}
                            />
                        </FieldLabel>

                        <FieldLabel label="Description" required error={errors.description}>
                            <TextArea
                                value={formData.description}
                                onChange={(e: ComponentEvent<string>) => updateField('description', e.value)}
                                placeholder="Provide a brief description of this exam"
                                rows={4}
                                disabled={loading}
                            />
                        </FieldLabel>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FieldLabel label="Exam Type" required error={errors.type}>
                                <Dropdown
                                    options={examTypes}
                                    value={formData.type}
                                    onChange={(e: ComponentEvent<string>) => updateField('type', e.value)}
                                    placeholder="Select exam type"
                                    disabled={loading}
                                />
                            </FieldLabel>

                            <FieldLabel label="Category">
                                <Dropdown
                                    options={categories}
                                    value={formData.category}
                                    onChange={(e: ComponentEvent<string>) => updateField('category', e.value)}
                                    placeholder="Select category"
                                    searchable
                                    disabled={loading}
                                />
                            </FieldLabel>
                        </div>

                        <FieldLabel label="Instructions" hint="Provide instructions that students will see before starting the exam">
                            <MarkdownEditor
                                value={formData.instructions || ''}
                                onChange={(value) => updateField('instructions', value)}
                                placeholder="Enter exam instructions..."
                                rows={6}
                                disabled={loading}
                            />
                        </FieldLabel>
                    </div>
                )}

                {currentStep === 1 && (
                    <div>
                        {errors.questions && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-200">
                                {errors.questions}
                            </div>
                        )}
                        <QuestionSelector
                            selectedQuestions={formData.questions}
                            onChange={(questions) => updateField('questions', questions)}
                            disabled={loading}
                        />
                    </div>
                )}

                {currentStep === 2 && (
                    <div>
                        <ExamSettings
                            settings={formData.settings}
                            onChange={(settings) => updateField('settings', settings)}
                            disabled={loading}
                        />
                    </div>
                )}

                {currentStep === 3 && (
                    <div className="space-y-6">
                        <div className="text-center py-8">
                            <span className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-600 text-white flex items-center justify-center">
                                <EyeIcon className="w-8 h-8" />
                            </span>
                            <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Review Your Exam</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Review all settings before saving</p>
                        </div>

                        <div className="space-y-4">
                            <div className="p-5 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Exam Details</h4>
                                <dl className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Title</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">{formData.title}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Type</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">{formData.type}</dd>
                                    </div>
                                    <div className="col-span-2">
                                        <dt className="text-slate-600 dark:text-slate-400">Description</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">{formData.description || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Category</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {categories.find((c) => c.value === formData.category)?.label || 'None'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Questions</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">{formData.questions.length}</dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="p-5 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Scheduling &amp; Access</h4>
                                <dl className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Duration</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {Math.floor((formData.settings?.duration || 3600) / 60)} minutes
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Access Control</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {(formData.settings?.accessControl || 'open_access').replace(/_/g, ' ')}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Max Attempts</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">{formData.settings?.maxAttempts ?? 1}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Start Date</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {formData.settings?.startDateTime
                                                ? new Date(formData.settings.startDateTime).toLocaleString()
                                                : 'Immediate'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="p-5 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Proctoring &amp; Grading</h4>
                                <dl className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Fullscreen Required</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {formData.settings?.fullscreenRequired ? 'Yes' : 'No'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Webcam Proctoring</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {formData.settings?.webcamProctoring ? 'Yes' : 'No'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Passing Score</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {formData.settings?.passingScore ?? 60}%
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-slate-600 dark:text-slate-400">Negative Marking</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {formData.settings?.negativeMarking ? 'Enabled' : 'Disabled'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
                <Button
                    label="Previous"
                    variant="default"
                    layout="outlined"
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0 || loading}
                />
                {currentStep < steps.length - 1 ? (
                    <Button
                        label="Next"
                        variant="primary"
                        onClick={() => {
                            if (!validateStep(currentStep)) {
                                showSnackbar('Please fix the errors before continuing', 'Validation Error', { type: 'error' });
                                return;
                            }
                            setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
                        }}
                        disabled={loading}
                    />
                ) : (
                    <Button
                        label="Save & Publish"
                        variant="primary"
                        onClick={() => handleSave(true)}
                        isLoading={saving}
                        disabled={saving || loading}
                    />
                )}
            </div>
        </div>
    );
};

export default ExamEditor;
